import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';

import { SynopticStandardUtils } from '@connect-services/client/components/synoptic/synoptic-graph/synoptic-standard-color.utils';
import { ColorEnum } from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.constants';
import { SynopticPlanItem } from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import { CassetteAnchorEditComponent } from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-edit/cassette-edit/cassette-anchor-edit/cassette-anchor-edit.component';
import { PositionEditModalResult } from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-edit/position-edit-modal/position-edit-modal.component';
import { CableDirectionEnum, SynopticPositionListValues } from '@connect-services/client/sdk/api';
import { SynopticPlanEditService } from '@connect-services/client/services/synoptic/edit/synoptic-plan-edit.service';
import { SynopticPlanEditModalService } from '@connect-services/client/services/synoptic/edit/synoptic-plan-edit-modal.service';
import { RessourcesModule } from '@ressources/client/ressources.module';

export enum ConnectorBorderStyleEnum {
    SOLID = 'solid',
    DASHED = 'dashed',
}

export type UpdatePositionOutputValue = { fiber: SynopticPlanItem; positionValue: PositionEditModalResult };

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CassetteAnchorEditComponent, RessourcesModule],
    selector: 'app-cassette-edit',
    styleUrls: ['../synoptic-plan-edit.component.scss', './cassette-edit.component.scss'],
    templateUrl: './cassette-edit.component.html',
})
export class CassetteEditComponent {
    public readonly cassette = input.required<SynopticPlanItem[]>();
    public readonly cassetteKey = input.required<string>();
    public readonly close = output<void>();
    public readonly open = output<void>();
    public readonly openedCassettesKeys = input.required<string[]>();
    public readonly positionListValues = input.required<SynopticPositionListValues>();
    public readonly remove = output<void>();
    public readonly updatePosition = output<UpdatePositionOutputValue>();

    protected readonly cassetteNumber = computed<string>(() => {
        return this.cassette()[0]?.numero_cassette ?? '0';
    });
    protected readonly connectedFibersRatio = computed<string>(() => {
        const fibersConnected = this.cassette().filter((item: SynopticPlanItem) => item.fibre_sortante);

        return `${fibersConnected.length}/${this.cassette().length}`;
    });
    protected readonly isEntranceSelectionEnabled = computed<boolean>(() => {
        const areAllEntrancePositionAssigned = this.cassette().every((item: SynopticPlanItem) => item.fibre_entrante);
        const tubeSelected = this.synopticPlanEditService.selectedTubeAnchor();

        return !areAllEntrancePositionAssigned && (!tubeSelected || tubeSelected.side === CableDirectionEnum.LEFT);
    });
    protected readonly isExitSelectionEnabled = computed<boolean>(() => {
        const areAllExitPositionAssigned = this.cassette().every((item: SynopticPlanItem) => item.fibre_sortante);
        const tubeSelected = this.synopticPlanEditService.selectedTubeAnchor();

        return !areAllExitPositionAssigned && (!tubeSelected || tubeSelected.side === CableDirectionEnum.RIGHT);
    });
    protected readonly isOpened = signal<boolean>(false);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly CableDirectionEnum = CableDirectionEnum;
    protected readonly synopticPlanEditModalService = inject(SynopticPlanEditModalService);
    protected readonly synopticPlanEditService = inject(SynopticPlanEditService);

    protected getConnectorizationBorder(fiber: SynopticPlanItem): Record<string, string> {
        const borderType = this.getBorderType(fiber);
        const incomingColor = this.getVisibleFiberColor(fiber.numero_fibre_entrante, borderType);
        const outgoingColor = this.getVisibleFiberColor(fiber.numero_fibre_sortante, borderType);

        return {
            borderBottom: `4px ${borderType} ${outgoingColor}`,
            borderLeft: `4px ${borderType} ${incomingColor}`,
            borderRight: `4px ${borderType} ${outgoingColor}`,
            borderTop: `4px ${borderType} ${incomingColor}`,
        };
    }

    protected getConnectorStyle(fiber: SynopticPlanItem): Record<string, string> {
        const borderType = this.getBorderType(fiber);

        return {
            '--borderBottom': `4px ${borderType} ${this.getVisibleFiberColor(fiber.numero_fibre_entrante, borderType)}`,
            '--borderTop': `4px ${borderType} ${this.getVisibleFiberColor(fiber.numero_fibre_sortante, borderType)}`,
        };
    }

    protected getFiberColor(fiberPosition?: string | null): string {
        if (!fiberPosition) {
            return ColorEnum.BLACK;
        }

        return SynopticStandardUtils.getStandardColor(Number(fiberPosition));
    }

    protected isCassetteSelected(side: CableDirectionEnum): boolean {
        const selectedElement = this.synopticPlanEditService.selectedCassetteAnchor();
        if (!selectedElement) {
            return false;
        }

        return selectedElement.key === this.cassetteKey() && selectedElement.side === side;
    }

    protected selectCassette(side: CableDirectionEnum): void {
        if (this.isCassetteSelected(side)) {
            this.synopticPlanEditService.selectedCassetteAnchor.set(null);

            return;
        }

        this.synopticPlanEditService.selectedCassetteAnchor.set({ key: this.cassetteKey(), side: side });
    }

    protected toggleCassetteDetails(): void {
        this.isOpened.update((previousValue: boolean) => !previousValue);
        if (this.isOpened()) {
            this.open.emit();

            return;
        }
        this.close.emit();
    }

    protected toggleCassetteSelection(): void {
        this.synopticPlanEditService.selectedCassettes.update((keys: Set<string>) => {
            const updated = new Set(keys);
            if (updated.has(this.cassetteKey())) {
                updated.delete(this.cassetteKey());
            } else {
                updated.add(this.cassetteKey());
            }

            return updated;
        });
    }

    protected async updatePositionValue(fiber: SynopticPlanItem): Promise<void> {
        const positionValue = await this.synopticPlanEditModalService.openPositionEditModal(
            this.positionListValues(),
            fiber,
        );
        if (!positionValue) {
            return;
        }

        this.updatePosition.emit({ fiber, positionValue });
    }

    private getBorderType(fiber: SynopticPlanItem): ConnectorBorderStyleEnum {
        return fiber.est_en_continuite ? ConnectorBorderStyleEnum.SOLID : ConnectorBorderStyleEnum.DASHED;
    }

    private getVisibleFiberColor(
        fiberPosition: string | null | undefined,
        borderType: ConnectorBorderStyleEnum,
    ): string {
        const color = this.getFiberColor(fiberPosition);

        return borderType === ConnectorBorderStyleEnum.DASHED && color === ColorEnum.WHITE ? ColorEnum.BLACK : color;
    }
}
